import React, { useState, useEffect } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Truck, Search, Phone, Mail, MapPin, DollarSign, Clock, CheckCircle, MessageCircle, MoreVertical, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminDrivers = () => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingDriver, setEditingDriver] = useState(null);

    useEffect(() => {
        fetchDrivers();
    }, []);

    const fetchDrivers = async () => {
        try {
            const response = await axios.get(`${API}/drivers/list`);
            setDrivers(response.data.drivers || []);
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (driverId) => {
        try {
            await axios.post(`${API}/drivers/approve-driver/${driverId}`);
            toast.success("Kont chofè a apwouve ak siksè!");
            fetchDrivers();
        } catch (error) {
            toast.error("Erè nan apwobasyon kont lan.");
        }
    };

    const handleDelete = async (driverId) => {
        if (window.confirm("Eske ou asire ou vle efase chofè sa nèt?")) {
            try {
                await axios.delete(`${API}/drivers/admin/delete/${driverId}`);
                toast.success("Chofè a efase ak siksè!");
                fetchDrivers();
            } catch (error) {
                toast.error("Erè lè nap efase chofè a.");
            }
        }
    };

    const handleUpdateDriver = async () => {
        try {
            await axios.put(`${API}/drivers/admin/update/${editingDriver.id}`, editingDriver);
            toast.success("Enfòmasyon chofè a ajou!");
            setEditingDriver(null);
            fetchDrivers();
        } catch (error) {
            toast.error("Erè lè nap modifye chofè a.");
        }
    };

    const filteredDrivers = drivers.filter(d => 
        d.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        d.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.phone?.includes(searchTerm)
    );

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic flex items-center gap-3">
                            <Truck className="w-8 h-8 text-[#FF9900]" /> Jere <span className="text-[#FF9900]">Chofè</span> Yo
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs mt-2">Lis tout chofè ki enskri pou livrezon yo.</p>
                    </div>
                    
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Chèche non, imèl oswa telefòn..."
                            className="pl-12 h-14 rounded-2xl bg-white border-none shadow-sm font-bold"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredDrivers.map(driver => (
                        <Card key={driver.id} className="rounded-[40px] border-none shadow-xl overflow-hidden bg-white border border-gray-100 group">
                            <CardHeader className="p-8 pb-4 bg-gray-50/50 border-b border-gray-50 flex flex-row items-center gap-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${driver.is_online ? 'bg-green-500 shadow-green-100' : 'bg-gray-200 shadow-gray-100'}`}>
                                    <Truck className="w-7 h-7 text-white" />
                                </div>
                                <div className="flex-1">
                                    <CardTitle className="text-xl font-black italic tracking-tighter text-gray-900 leading-none">{driver.fullname}</CardTitle>
                                    <div className="flex items-center gap-2 mt-2">
                                        <div className={`w-2 h-2 rounded-full ${driver.is_online ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                                        <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{driver.is_online ? 'AN LIY' : 'OFFLINE'}</span>
                                        <span className={`ml-2 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter ${driver.is_active ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'}`}>
                                            {driver.is_active ? 'AKTIF' : 'PANDING'}
                                        </span>
                                    </div>
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="w-8 h-8 p-0 rounded-full hover:bg-white/50">
                                            <MoreVertical className="w-4 h-4 text-gray-400" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="rounded-2xl border border-gray-100 shadow-xl p-2 min-w-[160px] bg-white">
                                        <DropdownMenuItem onClick={() => setEditingDriver(driver)} className="rounded-xl text-sm font-bold gap-2 focus:bg-[#FF9900]/10 focus:text-[#FF9900] cursor-pointer">
                                            <Edit className="w-4 h-4" /> Modifye
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleDelete(driver.id)} className="rounded-xl text-sm font-bold gap-2 text-red-500 focus:bg-red-50 focus:text-red-600 cursor-pointer">
                                            <Trash2 className="w-4 h-4" /> Efase Chofè
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </CardHeader>
                            <CardContent className="p-8 space-y-6">
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                                            <Phone className="w-4 h-4 text-[#FF9900]" /> {driver.phone}
                                        </div>
                                        <a 
                                            href={`https://wa.me/${driver.phone?.replace(/\D/g, '')}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-all shadow-sm border border-green-100"
                                            title="Kontakte sou WhatsApp"
                                        >
                                            <MessageCircle className="w-5 h-5 font-black" />
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                                        <Mail className="w-4 h-4 text-[#FF9900]" /> {driver.email}
                                    </div>
                                    <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                                        <MapPin className="w-4 h-4 text-[#FF9900]" /> {driver.department}, {driver.commune}
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Bous (Wallet)</span>
                                        <span className="text-2xl font-black italic tracking-tighter text-gray-900">{(driver.wallet_balance || 0).toLocaleString()} <span className="text-xs">HTG</span></span>
                                    </div>
                                    <div className="text-right flex flex-col items-end gap-2">
                                        <div>
                                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block text-right">Veyikil</span>
                                            <div className="font-black text-xs uppercase text-[#FF9900] italic">{driver.vehicle_type || "Moto"}</div>
                                        </div>
                                        {!driver.is_active && (
                                            <Button 
                                                onClick={() => handleApprove(driver.id)}
                                                className="h-8 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest mt-2"
                                            >
                                                Pase Kont
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {filteredDrivers.length === 0 && !loading && (
                    <div className="py-32 text-center opacity-30">
                        <Truck className="w-20 h-20 mx-auto mb-6" />
                        <h3 className="text-xl font-black italic uppercase tracking-tighter">Okenn chofè pa jwenn</h3>
                    </div>
                )}

                <Dialog open={!!editingDriver} onOpenChange={(open) => !open && setEditingDriver(null)}>
                    <DialogContent className="rounded-[30px] p-8 max-w-md bg-white border-0 shadow-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-black italic uppercase text-gray-900">Modifye Chofè</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-2">Non Chofè a</label>
                                <Input value={editingDriver?.fullname || ''} onChange={(e) => setEditingDriver({...editingDriver, fullname: e.target.value})} className="mt-1 h-14 rounded-2xl bg-gray-50 border-transparent focus:bg-white font-bold" />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-2">Telefòn</label>
                                <Input value={editingDriver?.phone || ''} onChange={(e) => setEditingDriver({...editingDriver, phone: e.target.value})} className="mt-1 h-14 rounded-2xl bg-gray-50 border-transparent focus:bg-white font-bold" />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-2">Veyikil (Moto, Machine, vs)</label>
                                <Input value={editingDriver?.vehicle_type || ''} onChange={(e) => setEditingDriver({...editingDriver, vehicle_type: e.target.value})} className="mt-1 h-14 rounded-2xl bg-gray-50 border-transparent focus:bg-white font-bold uppercase" />
                            </div>
                            <Button onClick={handleUpdateDriver} className="w-full h-14 rounded-2xl bg-[#FF9900] hover:bg-[#FF9900]/90 text-white font-black italic uppercase tracking-widest mt-4 shadow-xl shadow-[#FF9900]/20">
                                Sove Chanjman
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};
